'use client';
import NextImage from 'next/image';
import { useSearchParams, useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { motion } from 'motion/react';
import { cn } from 'lib/utils';
import { type ImageField } from 'lib/types';
import {
  type BloomProps,
  Bloom,
  Button,
  Icon,
  HubSpotForm,
  SelectField,
  Wrapper,
} from 'ui';

interface HubSpotForms {
  formId: string;
  label: string;
  value: string;
}

const hubSpotForms: HubSpotForms[] = [
  {
    label: 'Health plans',
    formId: '41b6b577-07f6-4ded-b8eb-e9e03cc7e46a',
    value: 'HealthPlans',
  },
  {
    label: 'Healthcare providers',
    formId: '41b6b577-07f6-4ded-b8eb-e9e03cc7e46a',
    value: 'HealthcareProviders',
  },
  {
    label: 'Research',
    formId: '4af04e96-54b5-49a3-b22d-37f5dcd9a2fa',
    value: 'Research',
  },
  {
    label: 'Corporate gifting',
    formId: 'f57de0e4-6a7f-49db-9616-1fe991342bb7',
    value: 'CorporateGifting',
  },
  {
    label: 'Government & defense',
    formId: '92e860d3-8b11-4501-9618-68fa4d4eefe9',
    value: 'GovernmentAndDefense',
  },
  {
    label: 'Partners & resellers',
    formId: '5f672240-9c88-40e6-9936-14bc078f5957',
    value: 'PartnersAndResellers',
  },
  {
    label: 'Athlete performance',
    formId: 'e151be5d-3a8c-4088-9a77-fe5b6761ddb1',
    value: 'AthletePerformance',
  },
  {
    label: 'Employee benefits & programs',
    formId: 'ac8edb12-0039-4099-b61a-59202fa25cb8',
    value: 'EmployeeBenefitsAndPrograms',
  },
  {
    label: 'Wellness & coaching',
    formId: 'ac8edb12-0039-4099-b61a-59202fa25cb8',
    value: 'WellnessAndCoaching',
  },
];

export type HubSpotFormSplitProps = {
  className?: string;
  alignment?: 'left' | 'right';
  title?: string;
  image: ImageField;
  bloom1?: BloomProps;
  bloom2?: BloomProps;
  bloom3?: BloomProps;
};

const HubSpotFormSplit = ({
  className,
  alignment = 'right',
  title,
  image,
  bloom1,
  bloom2,
  bloom3,
}: HubSpotFormSplitProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [selectedTopic, setSelectedTopic] = useState('');

  const topicParam = searchParams.get('topic');
  const selectedTopicData = hubSpotForms.find(
    (form) => form.value === topicParam
  );

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTopic) {
      const params = new URLSearchParams(searchParams);
      params.set('topic', selectedTopic);
      params.set('utm_source', selectedTopic);
      router.push(`?${params.toString()}`, { scroll: false });
    }
  };

  const renderFormSelector = () => (
    <form onSubmit={handleFormSubmit} className="flex flex-col">
      {!!title && <h3 className="text-p1 text-white mb-6">{title}</h3>}

      <SelectField
        className="mb-10"
        label="Select a solution area"
        value={selectedTopic}
        onChange={(e) => setSelectedTopic(e.target.value)}
        isRequired
      >
        <option value="">Choose an option below</option>
        {hubSpotForms.map((form) => (
          <option key={form.value} value={form.value}>
            {form.label}
          </option>
        ))}
      </SelectField>

      <div>
        <Button
          type="submit"
          disabled={!selectedTopic}
          variant="secondary"
          trackingProps={{
            event: 'solution_area_selected',
            properties: {
              selection: String(selectedTopic),
              location: 'body',
              path: '/contact',
              action: 'go to next step',
            },
          }}
        >
          <span>Next</span>
          <Icon
            name="internal-link-arrow-right"
            className="w-3.5 h-auto shrink-0"
          />
        </Button>
      </div>
    </form>
  );

  const renderHubSpotForm = () => {
    if (!selectedTopicData) return null;

    return (
      <HubSpotForm
        formId={selectedTopicData.formId}
        portalId="7217787"
        formInstanceId="4372"
        pageName="Oura Enterprise Solution"
        hsFormKey="5248b35df1eaca7f04726f2a4b848e38"
        contentType="landing-page"
        formsBaseUrl="/_hcms/forms/"
        trackingEventName={`Contact Form: ${selectedTopicData.label || 'Unknown Form'}`}
        trackingLocation="contact_page_split_form"
      />
    );
  };
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <Wrapper
        variant="page-full"
        className={cn(
          'relative flex flex-col gap-12 lg:gap-16 xl:gap-20 lg:items-start',
          {
            'lg:flex-row': alignment === 'left',
            'lg:flex-row-reverse': alignment === 'right',
          },
          className
        )}
      >
        <Bloom {...bloom1} />
        <Bloom {...bloom2} />
        <Bloom {...bloom3} />

        {/* Content */}
        <div className="lg:grow lg:shrink lg:basis-1/2 flex flex-col gap-6 relative z-2 bg-graphite-950/30 p-6 lg:p-10 rounded-lg">
          {topicParam ? renderHubSpotForm() : renderFormSelector()}
        </div>

        {/* Image */}
        <div className="lg:grow lg:shrink lg:basis-1/2 lg:max-w-150 relative z-2 overflow-hidden rounded-lg">
          <NextImage
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            className="w-full h-auto object-contain rounded-lg"
            role="presentation"
          />

          {/* Color Gradient */}
          <div
            className="z-2 absolute bottom-0 right-0 h-full w-full max-w-240 bg-gradient-to-l from-graphite-950/20 to-transparent"
            aria-hidden="true"
          />
        </div>
      </Wrapper>
    </motion.section>
  );
};

export default HubSpotFormSplit;
